'use client'
import SearchBar from '@/travelmde/components/Common/SearchBar'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import getDataBySlug from '@/travelmde/utilities/getDataBySlug'
import getDataById from '@/travelmde/utilities/getDataById'
import { useState, useEffect, useRef } from 'react'
import getCachedDataBySlug from '@/travelmde/utilities/getCachedDataBySlug'

const HeaderSearch = ({ lang, languages, title }) => {
  const router = useRouter()
  const [isOpen, setIsOpen] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState(lang)
  const dropdownRef = useRef(null) // Create a ref for the dropdown

  // Effect to handle clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleLanguageClick = async (language) => {
    // language is received in the form of 'en-US'
    setSelectedLanguage(language)
    setIsOpen(false)

    // we have following paths
    // /article/:slug
    // /event/:slug
    // /place/:slug
    // /static/:slug
    // /trending-article/:slug
    // all the rest of the paths can have language replacement
    const collectionsBasedOnPaths = {
      article: 'article',
      event: 'event',
      place: 'place',
      static: 'page',
      'trending-article': 'trendingArticle',
    }
    // the collection will be in the url like this /[collection]/
    // set the language in the url
    // get the current path
    let newPath = ''
    let currentPath = window.location.pathname
    // sample path /fr-FR/article/en-more-speed-while-surfing-at-sea
    // remove any trailing slash
    if (currentPath.endsWith('/')) currentPath = currentPath.slice(0, -1)
    // check if there is collection in the path based on collectionsBasedOnPaths
    for (const c of Object.keys(collectionsBasedOnPaths)) {
      const cPath = `/${c}/`
      if (currentPath.includes(cPath)) {
        const slug = currentPath.split('/').pop()
        const oldItem = await getCachedDataBySlug(lang, collectionsBasedOnPaths[c], slug)
        const item = await getDataById(language, collectionsBasedOnPaths[c], oldItem.id)
        if (!item) return
        newPath = `/${language}/${c}/${item.slug}`
        router.push(newPath)
        return
      }
    }

    // we have the case where we can simpley replace the language
    // find the lang in current path and replace with lan
    newPath = currentPath.replace(`/${lang}`, `/${language}`)
    router.push(newPath)
  }

  const currentLanguage = languages?.find((language) => language.language === selectedLanguage)

  return (
    <div className="header__search">
      <div
        className="menu__language-container cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
        ref={dropdownRef}
      >
        <div className="menu__language selected">
          <Image
            className="language__image"
            src={currentLanguage.flag.src}
            alt={currentLanguage.name}
            width={currentLanguage.flag.width}
            height={currentLanguage.flag.height}
          />
          <span>| {currentLanguage.shortkey}</span>
          <div className="language__dropdown-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="11"
              height="12"
              viewBox="0 0 11 12"
              fill="none"
            >
              <path
                d="M9.1867 4.64453L6.28883 7.5424C5.9466 7.88463 5.38658 7.88463 5.04435 7.5424L2.14648 4.64453"
                stroke="#2B303A"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
        <div className={`language__dropdown ${isOpen ? '!block' : '!hidden'}`}>
          {languages.map((language) => (
            <div
              className="menu__language"
              key={language.language}
              onClick={() => handleLanguageClick(language.language)}
            >
              <Image
                className="language__image"
                src={language.flag.src}
                alt={language.name}
                width={language.flag.width}
                height={language.flag.height}
              />
              <span>| {language.name}</span>
            </div>
          ))}
        </div>
      </div>

      <SearchBar lang={lang} title={title} />
    </div>
  )
}

export default HeaderSearch
